import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton
} from "@mui/material";

import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import CountdownTimer from "./CountdownTimer";

export default function InsuredCamera({
  connected,
  remoteVideoRef,
  surveyorVideoRef,
  startRecording,
  insuredInfo,
  address,
  recording,
  progress,
  videoStyle,
  mode,
  setMode,
  handleCapture,
  updateVideoBounds,
  peerId,
  pauseRecording,
  resumeRecording,
  stopRecording,
  isReconnecting,
  paused
}) {

  const [showUI, setShowUI] = useState(false);

  const [location, setLocation] = useState("Fetching location...");

  const [addressReady, setAddressReady] = useState(false);

  const [videoRatio, setVideoRatio] = useState(9 / 16);

  const lastFetchRef = useRef("");

  const apiKey = "AIzaSyAbaQ3ldMRUYmxt39TdasRdU-rtPLyZVhc";

  /* ------------------------------------------------------- */
  /* LOCATION */
  /* ------------------------------------------------------- */

  useEffect(() => {

    if (insuredInfo?.lat && insuredInfo?.lng) {

      const key =
        `${insuredInfo.lat.toFixed(5)},${insuredInfo.lng.toFixed(5)}`;

      if (lastFetchRef.current === key) return;

      lastFetchRef.current = key;

      getAddressFromLatLng(
        insuredInfo.lat,
        insuredInfo.lng
      ).then((addr) => {

        setLocation(addr);

        if (addr && addr !== "Unknown location") {
          setAddressReady(true);
        }
      });
    }

  }, [insuredInfo?.lat, insuredInfo?.lng]);

  async function getAddressFromLatLng(lat, lng) {

    

    try {

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
      );

      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        return data.results[0].formatted_address;
      }

      return "Unknown location";

    } catch (err) {

      return "Location fetch failed";
    }
  }

  /* ------------------------------------------------------- */
  /* UI SHOW */
  /* ------------------------------------------------------- */

  useEffect(() => {

    if (connected) {

      const timer = setTimeout(() => {
        setShowUI(true);
      }, 1000);

      return () => clearTimeout(timer);

    } else {

      setShowUI(false);
    }

  }, [connected]);

  /* ------------------------------------------------------- */
  /* VIDEO SIZE DETECTION */
  /* ------------------------------------------------------- */

  const handleVideoLoaded = () => {

    const video =
      (surveyorVideoRef && surveyorVideoRef.current)
      || (remoteVideoRef && remoteVideoRef.current);

    if (!video) return;

    const width = video.videoWidth;
    const height = video.videoHeight;

    if (width && height) {
      setVideoRatio(width / height);
    }

    if (updateVideoBounds) {
      updateVideoBounds();
    }
  };

  return (
    <Box
      sx={{
        flex: { xs: 1, md: 4 },
        height: "100%",
        minHeight: 0,
      }}
    >

      <Paper
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: 1,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >

        {/* HEADER */}
        <Box
          sx={{
            p: 1,
            bgcolor: "#eee",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6">
            Insured Camera
          </Typography>
        </Box>

        {/* MAIN AREA */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            bgcolor: "#111",

            overflow: "hidden",

            width: "100%",
            height: "100%",

            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >

          {/* ------------------------------------------------ */}
          {/* INSURED LABEL OUTSIDE FRAME */}
          {/* ------------------------------------------------ */}
          {connected && showUI && (
            <Box
              sx={{
                position: "absolute",

                top: 12,

                left: 12,

                zIndex: 50,

                bgcolor: "rgba(0,0,0,0.75)",

                px: 1.5,
                py: 0.7,

                borderRadius: "999px",

                backdropFilter: "blur(6px)",

                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                Insured
              </Typography>
            </Box>
          )}

          {/* ------------------------------------------------ */}
          {/* TIMER OUTSIDE VIDEO FRAME */}
          {/* ------------------------------------------------ */}
          {recording && (
            <Box
              sx={{
                position: "absolute",

                top: "90%",

                right: 12,

                transform: "translateY(-50%)",

                zIndex: 60,

                display: "flex",
                flexDirection: "column",
                alignItems: "center",

                gap: 1,

                width: 300,
              }}
            >

              <CountdownTimer
                initialSeconds={120}
                mode={mode}
                recording={recording}
                paused={paused}
                onComplete={() =>
                  console.log("Recording Finished")
                }
              />

            </Box>
          )}

          {/* ------------------------------------------------ */}
          {/* DYNAMIC VIDEO FRAME */}
          {/* ------------------------------------------------ */}

          <Box
            sx={{
              position: "relative",

              height: "100%",

              aspectRatio: `${videoRatio}`,

              maxWidth: "100%",
              maxHeight: "100%",

              bgcolor: "#000",

              overflow: "hidden",

              borderRadius: {
                xs: 0,
                sm: 2,
              },

              flexShrink: 0,
            }}
          >

            {/* VIDEO */}
            <video
              ref={surveyorVideoRef || remoteVideoRef}
              autoPlay
              playsInline
              onLoadedMetadata={handleVideoLoaded}
              style={{
                position: "absolute",
                inset: 0,

                width: "100%",
                height: "100%",

                objectFit: "cover",

                backgroundColor: "#000",
              }}
            />

            {/* WAITING */}
            {!connected && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,

                  zIndex: 100,

                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",

                  bgcolor: "rgba(0,0,0,0.85)",
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: 18,
                  }}
                >
                  Waiting for Insured...
                </Typography>
              </Box>
            )}

            {/* RECONNECTING OVERLAY */}
            {isReconnecting && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,

                  zIndex: 120,

                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",

                  bgcolor: "rgba(0,0,0,0.45)",

                  backdropFilter: "blur(3px)",
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",

                    fontSize: 22,

                    fontWeight: 700,

                    letterSpacing: 0.5,

                    textShadow: "0 2px 10px rgba(0,0,0,0.5)",
                  }}
                >
                  Reconnecting...
                </Typography>
              </Box>
            )}

            {/* ------------------------------------------------ */}
            {/* VIDEO OVERLAY */}
            {/* ------------------------------------------------ */}

            {connected && showUI && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,

                  zIndex: 20,

                  pointerEvents: "none",
                }}
              >

                {/* TOP BAR */}
                <Box
                  sx={{
                    position: "absolute",

                    top: 12,
                    left: 12,
                    right: 12,

                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",

                    zIndex: 20,
                  }}
                >

                  {/* LIVE */}
                  {/* <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,

                      bgcolor: "rgba(0,0,0,0.45)",

                      px: 1.2,
                      py: 0.6,

                      borderRadius: "999px",

                      backdropFilter: "blur(6px)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,

                        borderRadius: "50%",

                        bgcolor: "red",

                        animation: "pulse 1.5s infinite",
                      }}
                    />

                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      LIVE
                    </Typography>
                  </Box> */}

                  {/* INFO */}
                  {insuredInfo && (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end", // RIGHT SIDE
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "72%",
                          display: "flex",
                          alignItems: "stretch",
                          bgcolor: "rgba(0,0,0,0.42)",
                          borderRadius: "14px",
                          overflow: "hidden",
                          backdropFilter: "blur(8px)",
                          boxShadow: "0 3px 10px rgba(0,0,0,0.18)",
                          border: "1px solid rgba(255,255,255,0.06)",
                        }}
                      >
                        {/* MAP */}
                        <Box
                          sx={{
                            width: 92,
                            minHeight: "100%", // RESPONSIVE HEIGHT
                            alignSelf: "stretch",
                            flexShrink: 0,
                            position: "relative",
                            bgcolor: "#111",
                            overflow: "hidden",
                            borderTopLeftRadius: "14px",
                            borderBottomLeftRadius: "14px",
                            borderRight: "1px solid rgba(255,255,255,0.06)",
                          }}
                        >
                          {insuredInfo?.lat && insuredInfo?.lng ? (
                            <img
                              src={`https://maps.googleapis.com/maps/api/staticmap?center=${insuredInfo.lat},${insuredInfo.lng}&zoom=15&size=300x300&maptype=roadmap&markers=color:red%7C${insuredInfo.lat},${insuredInfo.lng}&key=${apiKey}`}
                              alt="Location Map"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                                position: "absolute",
                                inset: 0,
                              }}
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/300x300/1a1a1a/ffffff?text=No+Map";
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#777",
                                fontSize: 10,
                              }}
                            >
                              No Map
                            </Box>
                          )}

                          {/* LIVE */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 5,
                              left: 5,
                              bgcolor: "#ff3b3b",
                              color: "#fff",
                              px: 0.6,
                              py: "1px",
                              borderRadius: "999px",
                              fontSize: 8,
                              fontWeight: 700,
                              lineHeight: 1.4,
                              zIndex: 2,
                            }}
                          >
                            LIVE
                          </Box>
                        </Box>

                        {/* INFO */}
                        <Box
                          sx={{
                            flex: 1,
                            px: 1,
                            py: 0.7,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            minWidth: 0,
                          }}
                        >
                          {/* TIME */}
                          <Box
                            sx={{
                              alignSelf: "flex-start",
                              bgcolor: "rgba(255,255,255,0.1)",
                              color: "#fff",
                              px: 0.8,
                              py: 0.2,
                              borderRadius: "999px",
                              fontSize: 9,
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            {insuredInfo?.time || new Date().toLocaleString()}
                          </Box>

                          {/* LOCATION */}
                          <Typography
                            sx={{
                              color: "#fff",
                              fontSize: 10.5,
                              lineHeight: 1.35,
                              wordBreak: "break-word",
                            }}
                          >
                            {location || "Location not available"}
                          </Typography>

                          {/* LAT LNG */}
                          <Typography
                            sx={{
                              color: "rgba(255,255,255,0.6)",
                              fontSize: 9,
                              mt: 0.5,
                              lineHeight: 1.3,
                              fontFamily: "monospace",
                            }}
                          >
                            {insuredInfo?.lat}, {insuredInfo?.lng}
                          </Typography>

                          {/* AID */}
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              color: "#ff5252",
                              mt: 0.4,
                              fontWeight: 700,
                            }}
                          >
                            AID: {peerId}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* CONTROLS */}
                <Box
                  sx={{
                    position: "absolute",

                    bottom: 24,

                    left: 0,

                    width: "100%",

                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",

                    zIndex: 20,

                    pointerEvents: "auto",
                  }}
                >

                  {/* MODE */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      mb: 1.5,
                    }}
                  >

                    <Typography
                      onClick={() => {

                        if (recording) return;

                        setMode("photo");
                      }}
                      sx={{
                        cursor:
                          recording
                            ? "not-allowed"
                            : "pointer",

                        opacity:
                          recording
                            ? 0.45
                            : 1,

                        color:
                          mode === "photo"
                            ? "#fff"
                            : "#b67804",

                        fontWeight:
                          mode === "photo"
                            ? 700
                            : 400,

                        borderBottom:
                          mode === "photo"
                            ? "2px solid #fff"
                            : "none",

                        fontSize: 15,

                        transition: "0.2s ease",
                      }}
                    >
                      PHOTO
                    </Typography>

                    <Typography
                      onClick={() => setMode("video")}
                      sx={{
                        cursor: "pointer",

                        color:
                          mode === "video"
                            ? "#fff"
                            : "#b67804",

                        fontWeight:
                          mode === "video"
                            ? 700
                            : 400,

                        borderBottom:
                          mode === "video"
                            ? "2px solid #fff"
                            : "none",

                        fontSize: 15,
                      }}
                    >
                      VIDEO
                    </Typography>
                  </Box>

                  {/* BUTTONS */}
                  {!recording ? (

                    <IconButton
                      onClick={
                        mode === "photo"
                          ? handleCapture
                          : startRecording
                      }
                      sx={{
                        width: 74,
                        height: 74,

                        border: "4px solid #fff",

                        bgcolor:
                          mode === "photo"
                            ? "transparent"
                            : "red",
                      }}
                    >
                      {mode !== "photo" && (
                        <FiberManualRecordIcon
                          sx={{
                            color: "#fff",
                            fontSize: 28,
                          }}
                        />
                      )}
                    </IconButton>

                  ) : (

                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                      }}
                    >

                      <IconButton
                        onClick={
                          paused
                            ? resumeRecording
                            : pauseRecording
                        }
                        sx={{
                          width: 60,
                          height: 60,

                          bgcolor: "#fff",
                        }}
                      >
                        {paused ? "▶️" : "⏸"}
                      </IconButton>

                      <IconButton
                        onClick={stopRecording}
                        sx={{
                          width: 60,
                          height: 60,

                          bgcolor: "red",

                          color: "#fff",
                        }}
                      >
                        ⏹
                      </IconButton>

                    </Box>
                  )}
                </Box>

                {/* ANIMATION */}
                <style>
                  {`
                    @keyframes pulse {
                      0% {
                        opacity: 1;
                        transform: scale(1);
                      }

                      50% {
                        opacity: 0.5;
                        transform: scale(1.3);
                      }

                      100% {
                        opacity: 1;
                        transform: scale(1);
                      }
                    }
                  `}
                </style>

              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}